import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Badge, Button, Card } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { DiwaAPI } from '@/api/client'
import { HelpHint } from '@/components/HelpHint'

// Plan & pricing — fed by /billing/plan, the SAME data the entitlement gates read.
// The upgrade button hands off to Stripe Checkout.

interface PlanCard {
  key: string
  name: string
  price_monthly: number | null
  price_yearly: number | null
  seat_price: number | null
  tagline: string
  features: string[]
  purchasable: boolean
  highlight?: boolean
}

interface PlanResponse {
  plan: string
  status: string
  catalog: PlanCard[]
}

function priceLabel(c: PlanCard, interval: 'month' | 'year'): string {
  if (c.price_monthly === null) return 'Custom'
  if (c.price_monthly === 0) return 'Free'
  const v = interval === 'year' ? c.price_yearly ?? c.price_monthly * 12 : c.price_monthly
  return `$${v}`
}

export function Pricing() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const { data } = useQuery({ queryKey: ['billing-plan'], queryFn: () => api.get<PlanResponse>('/billing/plan') })
  const [interval, setInterval] = useState<'month' | 'year'>('month')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const current = data?.plan ?? 'keeper'
  const cards = data?.catalog ?? []

  async function upgrade(key: string) {
    setBusy(key)
    setError(null)
    try {
      const r = await api.post<{ url: string }>('/billing/checkout', { plan: key, interval })
      if (r.url) window.location.href = r.url  // hand off to Stripe Checkout
      else setError('Checkout is not available yet — try again soon.')
    } catch (err) {
      const status = (err as { status?: number }).status
      setError(status === 503 ? 'Checkout is being set up — try again soon.' : "Couldn't start checkout — try again.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            <HelpHint term="plan">Plan</HelpHint> &amp; Pricing
          </div>
          <div className="dw-page-subtitle">
            One{' '}
            <HelpHint gloss="the shared machinery — springs and the lake — that every tier is built on">
              substrate
            </HelpHint>
            , priced by what you hold. Bring your own agents on every tier.
          </div>
        </div>
        <div className="dw-chip-row">
          <button type="button" className={`dw-chip dw-chip--toggle${interval === 'month' ? ' dw-chip--on' : ''}`}
                  onClick={() => setInterval('month')}>Monthly</button>
          <button type="button" className={`dw-chip dw-chip--toggle${interval === 'year' ? ' dw-chip--on' : ''}`}
                  onClick={() => setInterval('year')}>Yearly · 2 months free</button>
        </div>
      </div>

      {error && <div className="dw-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="dw-pricing-grid">
        {cards.map((c) => {
          const active = c.key === current
          return (
            <Card key={c.key}>
              <div className="dw-pricing-card-head">
                <div>
                  <div className="dw-pricing-name">{c.name}</div>
                  <div className="dw-pricing-price">
                    {priceLabel(c, interval)}
                    {c.price_monthly ? <span>/{interval === 'year' ? 'yr' : 'mo'}</span> : null}
                    {c.seat_price ? <span className="dw-inbox-detail"> + ${c.seat_price}/seat</span> : null}
                  </div>
                </div>
                {active ? <Badge variant="success">Current</Badge> : c.highlight ? <Badge variant="info">Popular</Badge> : null}
              </div>
              <div className="dw-pricing-description">{c.tagline}</div>
              <ul className="dw-tier-features">
                {c.features.map((f) => <li key={f}>{f}</li>)}
              </ul>
              {active ? (
                <Button variant="primary" size="sm" disabled>Current plan</Button>
              ) : c.purchasable ? (
                <Button variant="primary" size="sm" disabled={busy === c.key} onClick={() => upgrade(c.key)}>
                  {busy === c.key ? 'Starting…' : `Choose ${c.name}`}
                </Button>
              ) : c.price_monthly === null ? (
                <a className="dw-btn" href="mailto:ss@diwata.domains?subject=Sovereign%20plan">Contact us</a>
              ) : (
                <Button variant="ghost" size="sm" disabled>Your plan</Button>
              )}
            </Card>
          )
        })}
      </div>

      <p className="dw-inbox-detail" style={{ marginTop: 16, maxWidth: 640 }}>
        Tala's failed runs never bill. Matching, alerts, and NAICS discovery are never metered —
        only missions are. Spend caps are on by default. Checkout runs through Stripe and is
        governed by the <Link to="/terms" className="dw-link">Terms of Service</Link> and{' '}
        <Link to="/privacy" className="dw-link">Privacy Policy</Link>.
      </p>
    </div>
  )
}
